/**
 * CoBrand — canonical co-branding string for the Meridian/Reignara redesign.
 *
 * Brand vocabulary (post-(m.6) Phase 1.5)
 * --------------------------------------
 * The platform brand is **reignara** — always lowercase, always
 * "reignara", never "Meridian/Reignara". The token "Meridian" is an
 * internal product / spec codename and is intentionally not surfaced
 * in the UI. Wherever the user-visible co-brand appears, it reads:
 *
 *     "Idaho America Pageants powered by reignara"
 *
 * The `reignara` mark itself uses the Bavex display face, NOT italic,
 * in the foreground (ink) color so it carries weight against any
 * surface in the system. Sized at 24px (`text-2xl`) — gives the
 * wordmark proper presence and stays legible against body type
 * (Bavex's tall stems and narrow bowls have a legibility floor
 * around 18-20px; 24px is the comfortable register).
 *
 * Three variants
 * --------------
 *   inline    — Single-line "Idaho America Pageants powered by reignara".
 *               Used in the public footer where IAP is the lead identity.
 *   stacked   — Two-line variant of the same. Backward-compat for older
 *               surfaces; new surfaces use `watermark` instead.
 *   watermark — `reignara` only. Bavex display, foreground ink color,
 *               non-italic. Used as a "powered-by" mark in the sidebar
 *               footer and auth surfaces where a co-equal IAP+reignara
 *               presentation would feel heavy.
 *
 * Surface mode (`surface` prop)
 * -----------------------------
 * The watermark is the only variant whose color flips with the
 * underlying surface. Two settings:
 *   - `light` (default): `reignara` rendered in `text-foreground` (ink).
 *   - `dark` : `reignara` rendered in `text-background` (cream / lightest
 *              token in the palette) — used on the dark slab surface
 *              that the Stream Chat workspace sidebar uses (the only
 *              dark-bg surface in the post-(m.0) system).
 *
 * Mounting (post-(m.6) sweep, Phase 2):
 * - Public Footer.tsx       — inline    (IAP-led, full attribution)
 * - SignIn / SignUp / Reset — watermark surface="light"
 * - Sidebar / AdminSidebar  — watermark surface="light"
 * - SlackWorkspaceShell     — watermark surface="dark"   (if mounted)
 *
 * Tokens & accessibility
 * ----------------------
 * - Color is sourced from design tokens only (no hex / rgb / hsl).
 * - The watermark phrase carries `aria-hidden="true"` because it is a
 *   decorative wordmark, not functional content.
 *
 * Feature: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 15.1, 15.2, 15.3, 15.4
 */

import type { JSX } from 'react';

import { cn } from '@/lib/utils';

// ── Tokenized phrase fragments ──────────────────────────────────────────────
const PRIMARY_BRAND = 'Idaho America Pageants';
const WATERMARK_LABEL = 'reignara';

// ── Component ───────────────────────────────────────────────────────────────

export type CoBrandVariant = 'inline' | 'stacked' | 'watermark';
export type CoBrandSurface = 'light' | 'dark';

export interface CoBrandProps {
  /** Layout variant. See module docblock. */
  variant?: CoBrandVariant;
  /**
   * Surface mode — controls wordmark contrast.
   * - `light` (default): foreground ink on light surface.
   * - `dark`           : background cream on dark surface.
   * Only consumed by the `watermark` variant; ignored by inline + stacked
   * which always render on light card surfaces.
   */
  surface?: CoBrandSurface;
  /** Extra Tailwind classes appended to the wrapping element. */
  className?: string;
}

/** Common wordmark span — Bavex display, NOT italic, sized for legibility. */
function WordmarkInline({ surface = 'light' }: { surface?: CoBrandSurface }): JSX.Element {
  return (
    <span
      className={cn(
        'font-wordmark text-2xl leading-none  tracking-tight',
        surface === 'dark' ? 'text-background' : 'text-foreground',
      )}
    >
      {WATERMARK_LABEL}
    </span>
  );
}

export function CoBrand({
  variant = 'inline',
  surface = 'light',
  className,
}: CoBrandProps): JSX.Element {
  if (variant === 'watermark') {
    // `reignara` wordmark — Bavex display, NOT italic. The wordmark
    // takes full visual weight via foreground ink (or cream on dark)
    // and 24px size. A "POWERED BY" eyebrow sits above the wordmark
    // — same caps + 0.2em tracking + muted-foreground treatment as
    // the Settings / Documents nav-row labels (so it visually
    // belongs to the same family as the rest of the sidebar
    // utility chrome) but rendered at 9px so the wordmark
    // dominates and the eyebrow reads as a footer signature.
    // aria-hidden because the wordmark is decorative.
    return (
      <span
        data-testid="co-brand-watermark"
        data-surface={surface}
        aria-hidden="true"
        className={cn('inline-flex flex-col items-center gap-1.5 leading-none', className)}
      >
        <span
          className={cn(
            'text-[9px] font-medium uppercase tracking-[0.2em]',
            surface === 'dark' ? 'text-background/60' : 'text-muted-foreground',
          )}
        >
          Powered by
        </span>
        <WordmarkInline surface={surface} />
      </span>
    );
  }

  if (variant === 'stacked') {
    return (
      <div className={cn('flex flex-col gap-1 text-sm leading-tight', className)}>
        <span className="font-semibold text-foreground">{PRIMARY_BRAND}</span>
        <span className="flex items-baseline gap-1.5 text-muted-foreground">
          powered by
          <WordmarkInline surface={surface} />
        </span>
      </div>
    );
  }

  return (
    <span className={cn('inline-flex items-baseline gap-1.5 text-sm', className)}>
      <span className="font-semibold text-foreground">{PRIMARY_BRAND}</span>
      <span className="text-muted-foreground">powered by</span>
      <WordmarkInline surface={surface} />
    </span>
  );
}

export default CoBrand;
